import { generateSlug } from "@/helpers/generateSlug";
import { getExpertiseCityStringsForLocale } from "@/lib/expertise/expertiseCityTree";
import type { ExpertiseCityStringSet } from "@/lib/expertise/expertiseCityTypes";
import { ExpertiseEnum } from "@/lib/expertiseEnum";
import { defaultLocale, type Locale } from "@/lib/i18n/locales";
import { LocationEnum } from "@/lib/locationEnum";
import { expertiseSlugs } from "@/lib/slRoutes";

export type {
  ExpertiseCityStringSet,
  Faqs,
  InsolvenzBericht,
  InsolvenzCase,
  InsolvenzSection,
  Judgement,
  Section,
} from "@/lib/expertise/expertiseCityTypes";

export { getExpertiseCityStringsForLocale } from "@/lib/expertise/expertiseCityTree";

export const cityNames: Record<LocationEnum, string> = {
  [LocationEnum.Stuttgart]: "Stuttgart",
  [LocationEnum.Landau]: "Landau (Pfalz)",
  [LocationEnum.Koblenz]: "Koblenz",
  [LocationEnum.München]: "München",
  [LocationEnum.Kaiserslautern]: "Kaiserslautern",
  [LocationEnum.Köln]: "Köln",
  [LocationEnum.Pirmasens]: "Pirmasens",
  [LocationEnum.Heilbronn]: "Heilbronn",
  [LocationEnum.FrankfurtAmMain]: "Frankfurt am Main",
};

export const getCitySlug = (city: LocationEnum): string => {
  return generateSlug(cityNames[city]);
};

export const getLocationEnumByCitySlug = (
  citySlug: string,
): LocationEnum | undefined => {
  for (const location of Object.values(LocationEnum)) {
    if (getCitySlug(location) === citySlug) {
      return location;
    }
  }
  return undefined;
};

/** German (default locale) tree — used for slugs, internal references, blog, etc. */
export const expertiseCityStrings =
  getExpertiseCityStringsForLocale(defaultLocale);

const expertiseSlugToEnum: Record<string, ExpertiseEnum> = Object.fromEntries(
  Object.entries(expertiseSlugs).map(([enumKey, slug]) => [
    slug,
    enumKey as ExpertiseEnum,
  ]),
);

export function getExpertiseEnumBySlug(
  slug: string,
): ExpertiseEnum | undefined {
  return expertiseSlugToEnum[slug];
}

export function getExpertiseCityBySlug(
  expertiseEnum: ExpertiseEnum,
  citySlug: string,
  locale: Locale = defaultLocale,
): ExpertiseCityStringSet | undefined {
  const expertiseCities =
    getExpertiseCityStringsForLocale(locale)[expertiseEnum];
  return Object.values(expertiseCities ?? {}).find(
    (city) => city.slug === citySlug,
  );
}

function expertiseCityTranslatableFingerprint(
  c: ExpertiseCityStringSet,
): string {
  const courtDesc = (c.courts ?? []).map((x) => x.description).join("\0");
  return [
    c.title,
    c.subtitle ?? "",
    c.description,
    c.metaTitle ?? "",
    c.metaDescription ?? "",
    c.imageAlt ?? "",
    c.teamMembersTitle ?? "",
    c.sectionTitle ?? "",
    c.courtsTitle ?? "",
    c.courtsDescription ?? "",
    c.notariesDescription ?? "",
    courtDesc,
    JSON.stringify(c.sections ?? []),
    JSON.stringify(c.faqs ?? null),
    JSON.stringify(c.insolvenzenSection ?? null),
  ].join("\u001f");
}

/**
 * Non-default locales only: `true` if merged content differs from German (real translation).
 * Default locale: `true` if the city exists in the catalog.
 */
export function expertiseCityHasLocaleTranslation(
  locale: Locale,
  expertiseEnum: ExpertiseEnum,
  citySlug: string,
): boolean {
  if (locale === defaultLocale) {
    return !!getExpertiseCityBySlug(expertiseEnum, citySlug, defaultLocale);
  }
  const deCity = getExpertiseCityBySlug(expertiseEnum, citySlug, defaultLocale);
  if (!deCity) {
    return false;
  }
  const locCity = getExpertiseCityBySlug(expertiseEnum, citySlug, locale);
  if (!locCity) {
    return false;
  }
  return (
    expertiseCityTranslatableFingerprint(locCity) !==
    expertiseCityTranslatableFingerprint(deCity)
  );
}
